import {
  Middleware,
  MiddlewareAPI,
  isRejectedWithValue,
} from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { apiToasts } from 'src/services/switch-rest-api/baseAPI.service';
import {
  type BulkItemResult,
  notifyBulkResults,
  pluralize,
} from 'src/utils/bulkResults';
import { formatReduxRTKMutation } from 'src/utils/formatters';

export const rtkQueryMiddleware: Middleware =
  (_api?: MiddlewareAPI) => (next) => async (action) => {
    if (action?.meta?.arg?.originalArgs?.silent === true) return next(action);

    const endpointName = action?.meta?.arg?.endpointName;
    const toastMeta = apiToasts[endpointName];

    const bulkResults = toastMeta?.bulkResults;
    if (bulkResults) {
      const type = action.type as string;
      if (type.endsWith('/pending') && toastMeta.pending) {
        toast.info(toastMeta.pending, { hideProgressBar: true });
      } else if (type.endsWith('/fulfilled')) {
        toast.dismiss();
        notifyBulkResults(
          action.payload as BulkItemResult[],
          bulkResults.noun,
          bulkResults.verb
        );
      } else if (type.endsWith('/rejected')) {
        toast.dismiss();
        toast.error(
          action?.payload?.data?.message ??
            `Failed to ${bulkResults.verb} ${pluralize(bulkResults.noun)}.`,
          { autoClose: 10000 }
        );
      }
      return next(action);
    }

    if (isRejectedWithValue(action)) {
      const status = action.payload.status;
      const message = action?.payload?.data?.message;
      const errorMessage = action?.payload?.data?.errorMessage;

      if (toastMeta?.error) {
        const customError = toastMeta.error[status];

        if (customError === 0) return next(action);

        toast.dismiss();

        toast.error(
          customError ?? errorMessage ?? message ?? 'An error occurred',
          {
            autoClose: toastMeta.autoClose ?? 10000,
          }
        );
      } else if (action?.meta?.arg?.endpointName) {
        toast.dismiss();
        toast.error(errorMessage ?? message ?? 'An error occurred', {
          autoClose: 10000,
        });
      }
    } else if (action?.meta?.arg?.endpointName) {
      const type = action.type;
      const isMutation = action?.meta?.arg?.type === 'mutation';

      if (type.endsWith('/fulfilled') && isMutation) {
        toast.dismiss();

        if (toastMeta && toastMeta.fulfilled !== undefined) {
          if (toastMeta.fulfilled === 0) return next(action);

          toast.success(toastMeta.fulfilled, {
            hideProgressBar: true,
            ...(toastMeta.autoClose !== undefined && {
              autoClose: toastMeta.autoClose,
            }),
          });
        } else {
          toast.success(action?.payload?.data?.message ?? 'Success!', {
            hideProgressBar: true,
          });
        }
      } else if (type.endsWith('/pending') && isMutation) {
        if (toastMeta && toastMeta.pending !== undefined) {
          if (toastMeta.pending === 0) return next(action);

          toast.info(toastMeta.pending, {
            hideProgressBar: true,
          });
        } else {
          toast.info(`${formatReduxRTKMutation(endpointName)}...`, {
            hideProgressBar: true,
          });
        }
      }
    }

    return next(action);
  };
